import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRecommenderBySlug } from '../hooks/useRecommenderBySlug';
import { isFullList } from '../lib/fullList';
import { DEFAULT_PAGE_TITLE } from '../lib/siteMeta';
import RestaurantList from './RestaurantList';
import Header from './Header';
import Footer from './Footer';
import Skeleton from './Skeleton';

const RestaurantListPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { recommender, isLoaded, error } = useRecommenderBySlug(slug);

  // Give each recommender's page its own title so shared links / tabs read
  // "Jordan's Top Five" instead of the generic site title.
  useEffect(() => {
    if (recommender) {
      document.title = `${recommender.name}'s Top Five | Rankum`;
    }
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
    };
  }, [recommender]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Header backTo="/recommenders" backLabel="All Recommenders" />
        <main className="flex-1 flex items-center justify-center px-6">
          <p className="text-charcoal-light" role="alert">
            Couldn&rsquo;t load this recommender: {error}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-cream">
        <Header backTo="/recommenders" backLabel="All Recommenders" />
        <div className="pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl" aria-busy="true" aria-label="Loading recommender">
              <Skeleton className="w-20 h-20 md:w-24 md:h-24 rounded-full mb-8" />
              <Skeleton className="h-12 md:h-16 w-2/3 mb-3" />
              <Skeleton className="h-12 md:h-16 w-1/2" />
            </div>
          </div>
        </div>
        <div className="container mx-auto px-6 pb-16">
          <div className="max-w-3xl border-t border-black/5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-6 md:gap-10 py-6 border-b border-black/5 last:border-b-0">
                <Skeleton className="h-12 w-12 md:h-16 md:w-16 flex-shrink-0" />
                <div className="flex-1 space-y-3 pt-2">
                  <Skeleton className="h-7 w-3/4" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!recommender) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <h1 className="font-display text-3xl text-charcoal mb-4">Recommender not found</h1>
          <Link to="/recommenders" className="text-amber hover:text-terracotta transition-colors">
            Back to recommenders
          </Link>
        </div>
      </div>
    );
  }

  const nameParts = recommender.name.split(' ');
  const initials = nameParts.map((n) => n[0] ?? '').join('');

  return (
    <div className="min-h-screen bg-cream">
      <Header backTo="/recommenders" backLabel="All Recommenders" />

      {/* Hero Section */}
      <div className="pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            {/* Avatar */}
            <div
              className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-amber/20 to-terracotta/20 border border-amber/30 flex items-center justify-center mb-8 animate-fade-in"
            >
              <span className="text-amber font-display text-2xl md:text-3xl">{initials}</span>
            </div>

            {/* Title */}
            <div className="animate-slide-up">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-4 tracking-tight">
                {recommender.name}'s
                <br />
                <span className="text-amber">Top Five</span>
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant List */}
      <div className="container mx-auto px-6 pb-16">
        <div className="max-w-3xl">
          {!isFullList(recommender.restaurants.length) ? (
            // Only full top fives are public. Until then the page exists
            // (so a signed-up friend's link doesn't 404) but keeps the
            // in-progress picks private, matching the sign-up promise that
            // a list shows up once all five restaurants are in.
            <p className="text-charcoal-light">
              {recommender.name} is still picking their top five. Check back
              soon.
            </p>
          ) : (
            <RestaurantList restaurants={recommender.restaurants} />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RestaurantListPage;
